//src/api/handleApiError.js
import { ERROR_CODES } from '../constants/errors'; // Assuming you have this

/*
 * Handles API request errors consistently.
 * @param {Error} error - The error object from Axios or other sources.
 * @param {string} functionName - Name of the calling function for logging.
 * @returns {object} Standardized error object { error: string, message: string }.
 */
export const handleApiError = (error, functionName) => {
  console.error(`[${functionName}] API Error:`, error);

  if (error.response) {
    const statusCode = error.response.status;
    const serverError = error.response.data?.error;
    const serverMessage = error.response.data?.message;
    console.error(`[${functionName}] Response Data:`, error.response.data);
    console.error(`[${functionName}] Response Status:`, error.response.status);
    // --- START OF KILL SWITCH LOGIC ---
    // These codes are returned by our new auth_middleware.py when access is blocked
    const killSwitchCodes = [
      'PAYMENT_REQUIRED', // Overdue 5-day grace period
      'LEGAL_REQUIRED', // New document version needs signature
      'ACCOUNT_SUSPENDED', // Manual admin suspension
      'INSUFFICIENT_FUNDS', // Wallet is empty
    ];

    if (statusCode === 403 && killSwitchCodes.includes(serverError)) {
      console.warn(
        `[${functionName}] ACCESS BLOCKED: ${serverError}. Forcing app re-validation.`,
      );

      // We use a short delay to allow any immediate logging to finish,
      // then reload. This forces DataContext to re-fetch the user profile,
      // which in turn triggers the hard-block UI in AppContent.js.
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
    // --- END OF KILL SWITCH LOGIC ---

    return {
      status: false,
      error: serverError || `HTTP_ERROR_${statusCode}`,
      message: serverMessage || 'An API error occurred.',
      statusCode: statusCode,
    };
  } else if (error.request) {
    // Request was made but no response received
    console.error(`[${functionName}] No response received:`, error.request);
    return {
      status: false,
      error: ERROR_CODES.NETWORK_ERROR,
      message: 'No response received from server. Check network connection.',
      statusCode: null,
    };
  } else {
    // Something happened in setting up the request
    console.error(`[${functionName}] Request setup error:`, error.message);
    return {
      status: false,
      error: ERROR_CODES.REQUEST_ERROR,
      message: error.message || 'Error setting up the request.',
      statusCode: null,
    };
  }
};

export const handleAxiosError = (
  error,
  defaultMessage = 'An API error occurred.',
) => {
  console.error('Axios Error:', error.response || error.message);
  const errorMessage =
    error.response?.data?.message || error.message || defaultMessage;
  const errorCode =
    error.response?.data?.error || ERROR_CODES.INTERNAL_SERVER_ERROR;
  return { status: false, message: errorMessage, error: errorCode };
};
